import React, { useState, useEffect } from 'react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { db, storage, auth, functions } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import './ArtistUpload.css';
import { createSongPostNotifications } from '../services/notificationService';

const ArtistUpload = ({ user, uid, onComplete }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [songName, setSongName] = useState('');
    const [confirmedUpload, setConfirmedUpload] = useState(false);
    const [showDetailsPage, setShowDetailsPage] = useState(false);
    const [currentAuthUserId, setCurrentAuthUserId] = useState(null);

    // Monitor auth state changes to ensure we always have the latest user
    useEffect(() => {
        // Get initial auth state
        if (auth.currentUser) {
            setCurrentAuthUserId(auth.currentUser.uid);
        }

        // Subscribe to auth state changes
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentAuthUserId(user.uid);
            } else {
                setCurrentAuthUserId(null);
            }
        });

        return () => unsubscribe();
    }, []);

    // Allowed audio formats
    const ALLOWED_FORMATS = {
        extensions: ['.mp3', '.aac', '.m4a', '.wav', '.flac', '.ogg', '.opus', '.aiff'],
        mimeTypes: [
            'audio/mpeg',
            'audio/mp4',
            'audio/x-m4a',
            'audio/wav',
            'audio/flac',
            'audio/ogg',
            'audio/opus',
            'audio/aiff',
            'audio/wave',
            'audio/x-wav'
        ]
    };

    // Blocked video formats - explicitly reject video files
    const BLOCKED_VIDEO_FORMATS = {
        extensions: ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.m4v', '.3gp', '.mpg', '.mpeg', '.m2v'],
        mimeTypes: [
            'video/mp4',
            'video/quicktime',
            'video/x-msvideo',
            'video/x-matroska',
            'video/webm',
            'video/x-flv',
            'video/x-ms-wmv',
            'video/3gpp',
            'video/mpeg',
            'video/x-m4v'
        ]
    };

    const validateAudioFile = (file) => {
        // First check if it's a video file - reject immediately
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const isVideoExtension = BLOCKED_VIDEO_FORMATS.extensions.includes(fileExtension);
        const isVideoMimeType = BLOCKED_VIDEO_FORMATS.mimeTypes.includes(file.type) || file.type.startsWith('video/');

        if (isVideoExtension || isVideoMimeType) {
            return {
                isValid: false,
                errors: {
                    isVideo: true,
                    extension: false,
                    mimeType: false,
                    size: false
                }
            };
        }

        // Check if it's a valid audio file
        const isValidExtension = ALLOWED_FORMATS.extensions.includes(fileExtension);
        const isValidMimeType = ALLOWED_FORMATS.mimeTypes.includes(file.type);

        // Check file size (max 100MB for high-quality audio)
        const isValidSize = file.size <= 100 * 1024 * 1024;

        return {
            isValid: isValidExtension && isValidMimeType && isValidSize,
            errors: {
                isVideo: false,
                extension: !isValidExtension,
                mimeType: !isValidMimeType,
                size: !isValidSize
            }
        };
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validation = validateAudioFile(file);

            if (!validation.isValid) {
                if (validation.errors.isVideo) {
                    setError('❌ Video files are not allowed! Please upload only audio files (MP3, M4A, WAV, FLAC, OGG, etc.)');
                    alert('❌ Video files are not allowed! Please upload only audio files (MP3, M4A, WAV, FLAC, OGG, etc.)');
                } else if (validation.errors.extension || validation.errors.mimeType) {
                    setError('❌ Only standard audio formats allowed (MP3, M4A, WAV, FLAC, OGG, etc.). Video files are not allowed.');
                    alert('❌ Only standard audio formats allowed (MP3, M4A, WAV, FLAC, OGG, etc.). Video files are not allowed.');
                } else if (validation.errors.size) {
                    setError('❌ File size must be less than 100MB');
                    alert('❌ File size must be less than 100MB');
                }
                setSelectedFile(null);
                return;
            }

            setSelectedFile(file);
            setError(null);
            // Auto-fill song name from file name (without extension)
            const fileNameWithoutExt = file.name.replace(/\.(mp3|aac|m4a|wav|flac|ogg|opus|aiff)$/i, '');
            setSongName(fileNameWithoutExt);
            // Show details page after file selection
            setShowDetailsPage(true);
        }
    };

    const handleUpload = async (file, userId) => {
        if (!confirmedUpload) {
            setError('Please confirm that you have the rights and permissions to upload this music.');
            return;
        }

        if (!file) {
            setError('Please select a file to upload');
            return;
        }

        // Check internet connection first
        if (!navigator.onLine) {
            setError('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
            alert('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
            return;
        }

        // Check if user is authenticated
        if (!auth.currentUser) {
            setError('❌ You must be logged in to upload songs. Please sign in and try again.');
            alert('❌ You must be logged in to upload songs. Please sign in and try again.');
            return;
        }

        // Force refresh auth token to ensure we have the latest user
        try {
            await auth.currentUser.getIdToken(true);
        } catch (tokenErr) {
            console.warn('Could not refresh auth token:', tokenErr);
        }

        // CRITICAL: Always use auth.currentUser.uid directly, don't trust userId parameter
        const actualLoggedInUserId = auth.currentUser?.uid;
        
        if (!actualLoggedInUserId || typeof actualLoggedInUserId !== 'string' || actualLoggedInUserId.trim().length === 0) {
            setError('❌ Authentication error. Please refresh the page and try again.');
            alert('❌ Authentication error. Please refresh the page and try again.');
            return;
        }

        // Double-check validation before upload
        const validation = validateAudioFile(file);
        if (!validation.isValid) {
            if (validation.errors.isVideo) {
                setError('❌ Video files are not allowed! Please upload only audio files.');
                alert('❌ Video files are not allowed! Please upload only audio files.');
            } else {
                setError('❌ Invalid file format. Please select a valid audio file (MP3, M4A, WAV, etc.). Video files are not allowed.');
                alert('❌ Invalid file format. Please select a valid audio file (MP3, M4A, WAV, etc.). Video files are not allowed.');
            }
            return;
        }

        try {
            setUploading(true);
            setError(null);
            setUploadProgress(0);

            // Upload to Firebase Storage with progress tracking
            let fileURL = null;
            try {
                // Sanitize file name - remove special characters and spaces
                const sanitizeFileName = (fileName) => {
                    // Get file extension
                    const ext = fileName.substring(fileName.lastIndexOf('.'));
                    // Get file name without extension
                    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
                    // Replace special characters with underscores and remove spaces
                    const sanitized = nameWithoutExt
                        .replace(/[^a-zA-Z0-9]/g, '_')
                        .replace(/_+/g, '_')
                        .replace(/^_|_$/g, '');
                    return sanitized + ext;
                };
                
                const sanitizedFileName = sanitizeFileName(file.name);
                // CRITICAL: Use actualLoggedInUserId for storage path, not userId parameter
                const path = `songs/${actualLoggedInUserId}/${Date.now()}_${sanitizedFileName}`;
                const storageRef = ref(storage, path);
                
                // Use resumable upload for progress tracking and better reliability
                const uploadTask = uploadBytesResumable(storageRef, file);
                
                // Track upload progress with longer timeout for better reliability
                const UPLOAD_TIMEOUT = 600000; // 10 minutes timeout for songs (allows for large files and slow connections)
                let uploadCompleted = false;
                let timeoutId = null;
                
                await new Promise((resolve, reject) => {
                    // Set timeout to prevent hanging - but don't cancel immediately
                    timeoutId = setTimeout(() => {
                        if (!uploadCompleted) {
                            // Check if upload is still in progress
                            const taskState = uploadTask.snapshot?.state;
                            if (taskState === 'running' || taskState === 'paused') {
                                // Upload is still active, give it more time
                                console.warn('Upload taking longer than expected, but still in progress...');
                                // Reset timeout for another 5 minutes
                                timeoutId = setTimeout(() => {
                                    if (!uploadCompleted) {
                                        uploadTask.cancel();
                                        reject(new Error('Upload timeout: The upload took too long. Please check your internet connection and try again with a smaller file.'));
                                    }
                                }, 300000); // Additional 5 minutes
                            } else {
                                // Upload seems stuck, cancel it
                                uploadTask.cancel();
                                reject(new Error('Upload timeout: Please check your internet connection and try again.'));
                            }
                        }
                    }, UPLOAD_TIMEOUT);
                    
                    uploadTask.on(
                        'state_changed',
                        (snapshot) => {
                            // Calculate progress percentage
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(Math.round(progress));
                            
                            // Reset timeout on progress - upload is active
                            if (timeoutId) {
                                clearTimeout(timeoutId);
                                timeoutId = setTimeout(() => {
                                    if (!uploadCompleted) {
                                        uploadTask.cancel();
                                        reject(new Error('Upload timeout: Please check your internet connection and try again.'));
                                    }
                                }, UPLOAD_TIMEOUT);
                            }
                        },
                        (error) => {
                            if (timeoutId) clearTimeout(timeoutId);
                            console.error('Upload error:', error);
                            console.error('Error code:', error.code);
                            console.error('Error message:', error.message);
                            
                            // Don't reject if it's a cancellation we initiated
                            if (error.code === 'storage/canceled' && uploadCompleted) {
                                return; // Already handled
                            }
                            
                            // Check if offline
                            if (!navigator.onLine) {
                                reject(new Error('📡 No Internet Connection\n\nPlease check your internet connection and try again.'));
                                return;
                            }
                            
                            let errorMessage = 'Upload failed. ';
                            if (error.code === 'storage/unauthorized') {
                                errorMessage += 'Permission denied. Please check Firebase Storage rules.';
                            } else if (error.code === 'storage/canceled') {
                                // Only show cancellation error if it wasn't due to timeout
                                if (!errorMessage.includes('timeout')) {
                                    errorMessage += 'Upload was canceled.';
                                }
                            } else if (error.code === 'storage/unknown' || error.code === 'storage/retry-limit-exceeded') {
                                errorMessage += '📡 Network error. Please check your internet connection and try again.';
                            } else if (error.message && (error.message.includes('ERR_FAILED') || error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('CORS'))) {
                                errorMessage += '📡 Network request failed. Please check your internet connection and try again.';
                            } else {
                                errorMessage += error.message || 'Please try again.';
                            }
                            reject(new Error(errorMessage));
                        },
                        async () => {
                            // Upload completed successfully
                            try {
                                if (timeoutId) clearTimeout(timeoutId);
                                uploadCompleted = true;
                                fileURL = await getDownloadURL(uploadTask.snapshot.ref);
                                resolve();
                            } catch (err) {
                                if (timeoutId) clearTimeout(timeoutId);
                                console.error('Error getting download URL:', err);
                                reject(new Error('Failed to get download URL: ' + (err.message || 'Unknown error')));
                            }
                        }
                    );
                });
            } catch (err) {
                console.error('Firebase upload failed', err);
                throw err;
            }

            // Create song document in Firebase Firestore
            // CRITICAL: ALWAYS use auth.currentUser.uid directly, don't trust userId parameter
            // Force refresh auth token to ensure we have the latest user
            try {
                if (auth.currentUser) {
                    await auth.currentUser.getIdToken(true);
                }
            } catch (tokenErr) {
                console.warn('Could not refresh auth token:', tokenErr);
            }
            
            const songTitle = songName.trim() || file.name.replace(/\.(mp3|aac|m4a|wav|flac|ogg|opus|aiff)$/i, '');
            
            await auth.authStateReady?.(); // forces firebase auth to be ready

            const finalUserId = auth.currentUser?.uid;

            if (!finalUserId) {
                alert("You are not logged in yet. Please wait and try again.");
                return;
            }
            
            const createSong = httpsCallable(functions, 'createSong');
            await createSong({
                rightsConfirmation: confirmedUpload,
                song: {
                title: songTitle,
                songUrl: fileURL,
                artistId: finalUserId,    // Use logged-in user's ID directly
                userId: finalUserId,      // Also save as userId for consistency
                ownerId: finalUserId,     // Also save as ownerId for compatibility
                artistUID: finalUserId,   // Also save as artistUID for compatibility
                artistName: (user && (user.displayName || user.name || user.username)) || 'Unknown Artist',
                genre: 'Unknown',
                uploadDate: new Date(),
                createdAt: serverTimestamp(),
                tipsTotal: 0,
                plays: 0,
                format: file.name.split('.').pop().toLowerCase(),
                size: file.size,
                mimeType: file.type
                }
            });

            // Update user document with flags and increment post count
            // Use finalUserId (logged-in user) not userId parameter
            const userRef = doc(db, 'users', finalUserId);
            await updateDoc(userRef, {
                hasUploadedSong: true,
                uploadedAt: new Date(),
                posts: increment(1)
            });

            // Create notifications for all listeners who follow this artist
            // Fire and forget - don't wait for notification creation
            // Use finalUserId (logged-in user) not userId parameter
            createSongPostNotifications(finalUserId, songTitle).catch(err => {
                console.error('Failed to create song post notifications:', err);
            });

            console.log('Song uploaded successfully:', fileURL);
            alert('✅ File uploaded successfully!');
            onComplete('uploaded');
        } catch (error) {
            console.error('Upload error:', error);
            // Check if offline
            if (!navigator.onLine) {
                setError('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
                alert('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
            } else {
                const errorMsg = error.message || 'Failed to upload song. Please try again.';
                setError(errorMsg);
                alert(`❌ ${errorMsg}`);
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleSkip = () => {
        onComplete('skipped');
    };

    // If file selected, show details page
    if (showDetailsPage && selectedFile) {
        return (
            <div className="upload-container">
                <div className="logo-section">
                    <div className="logo-container">
                        <span className="music-icon">🎵</span>
                        <h1 className="logo-text">HubTune</h1>
                    </div>
                    <p className="tagline">Your Music Hub</p>
                </div>

                <div className="upload-box">
                    <div className="skip-container">
                        <button 
                            className="skip-btn" 
                            onClick={() => {
                                setShowDetailsPage(false);
                                setSelectedFile(null);
                                setSongName('');
                                setConfirmedUpload(false);
                            }} 
                            title="Go back"
                        >
                            <span className="skip-icon">←</span>
                        </button>
                    </div>

                    <h1 className="upload-title">Song Details</h1>
                    <p className="upload-subtitle">Enter song name and confirm upload</p>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <div className="upload-area">
                        {/* Selected File Info */}
                        <div className="file-info" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#1a1a1a', borderRadius: '12px' }}>
                            <p className="file-name" style={{ color: '#fff', marginBottom: '0.5rem' }}>Selected: {selectedFile.name}</p>
                            <p className="file-size" style={{ color: '#888', fontSize: '0.875rem' }}>
                                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                        </div>

                        {/* Song Name Input */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label htmlFor="song-name" style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '500' }}>
                                Song Name
                            </label>
                            <input
                                type="text"
                                id="song-name"
                                value={songName}
                                onChange={(e) => setSongName(e.target.value)}
                                placeholder="Enter song name"
                                className="chat-input"
                                style={{ 
                                    width: '100%', 
                                    padding: '0.75rem 1rem',
                                    background: '#111',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '12px',
                                    color: '#fff',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        {uploading && (
                            <div className="upload-progress" style={{ marginBottom: '1rem' }}>
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                                <p className="progress-text">
                                    Uploading... {uploadProgress}%
                                </p>
                            </div>
                        )}

                        {/* Music rights confirmation */}
                        <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                            <input
                                type="checkbox"
                                id="confirm-upload"
                                checked={confirmedUpload}
                                onChange={(e) => setConfirmedUpload(e.target.checked)}
                                disabled={uploading}
                                style={{ 
                                    marginTop: '0.25rem',
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                    accentColor: '#ff3b3b'
                                }}
                            />
                            <label 
                                htmlFor="confirm-upload" 
                                style={{ 
                                    color: '#fff', 
                                    cursor: 'pointer',
                                    flex: 1,
                                    lineHeight: '1.5'
                                }}
                            >
                                I confirm that I own or have the necessary rights and permissions to upload and make this music available on HubTune. I understand that I am responsible for the content and rights associated with this upload.
                                <Link to="/music-rights" style={{ display: 'block', color: '#ff6b6b', textDecoration: 'underline', marginTop: '0.5rem' }}>
                                    View Music Rights &amp; Content Policy
                                </Link>
                            </label>
                        </div>

                        <button
                            className="upload-btn"
                            onClick={async () => {
                                if (!confirmedUpload) {
                                    setError('Please confirm that you have the rights and permissions to upload this music.');
                                    return;
                                }

                                // ALWAYS use logged-in user's ID for uploads - song belongs to the user who uploads it
                                // Force refresh auth token to ensure we have the latest user
                                try {
                                    // Force token refresh to ensure we have latest auth state
                                    if (auth.currentUser) {
                                        await auth.currentUser.getIdToken(true);
                                    }
                                } catch (tokenErr) {
                                    console.warn('Could not refresh auth token:', tokenErr);
                                }
                                
                                const userId = auth.currentUser?.uid;
                                
                                if (!userId) {
                                    alert('❌ Error: You must be logged in to upload songs. Please sign in and try again.');
                                    return;
                                }
                                
                                handleUpload(selectedFile, userId);
                            }}
                            disabled={!songName.trim() || !confirmedUpload || uploading}
                            style={{
                                opacity: (!songName.trim() || !confirmedUpload) ? 0.5 : 1,
                                cursor: (!songName.trim() || !confirmedUpload) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {uploading ? 'Uploading...' : 'Upload Song'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // File selection page
    return (
        <div className="upload-container">
            <div className="logo-section">
                <div className="logo-container">
                    <span className="music-icon">🎵</span>
                    <h1 className="logo-text">HubTune</h1>
                </div>
                <p className="tagline">Your Music Hub</p>
            </div>

            <div className="upload-box">
                <div className="skip-container">
                    <button className="skip-btn" onClick={handleSkip} title="Skip for now">
                        <span className="skip-icon">⏭</span>
                    </button>
                </div>

                <h1 className="upload-title">Upload Your First Song</h1>
                <p className="upload-subtitle">Share your music with the world</p>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <div className="upload-area">
                    <div className="file-input-container">
                        <input
                            type="file"
                            id="song-upload"
                            accept=".mp3,.aac,.m4a,.wav,.flac,.ogg,.opus,.aiff,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/flac,audio/ogg,audio/opus,audio/aiff"
                            onChange={handleFileSelect}
                            className="file-input"
                        />
                        <label htmlFor="song-upload" className="file-input-label">
                            <div className="upload-icon">🎵</div>
                            <span className="upload-text">
                                {selectedFile ? selectedFile.name : 'Choose Audio File'}
                            </span>
                            <span className="upload-hint">Audio only: MP3, M4A, WAV, FLAC, OGG, etc. (No video files) Max 100MB</span>
                        </label>
                    </div>

                    {selectedFile && !showDetailsPage && (
                        <div className="file-info">
                            <p className="file-name">{selectedFile.name}</p>
                            <p className="file-size">
                                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArtistUpload;
